import React, { useEffect, useRef, useState } from "react";
import removeRedEye from "../../../assets/icons/removeRedEye.svg";
import { Logistic } from "src/services/types";
import LogisticDetails from "./LogisticDetails"; // Adjust import path as needed
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import { format } from "date-fns";
import AdminService from "src/services/admin.services";
import CustomSelect from "../../../components/ui/CustomSelect";

// Nigerian state coordinates (approximate center points)
const getStateFromCoordinates = (
  latitude: number,
  longitude: number
): string => {
  const stateCoordinates: {
    [key: string]: { lat: number; lng: number; radius: number };
  } = {
    Abuja: { lat: 9.082, lng: 7.3986, radius: 10.0 },
    Lagos: { lat: 6.5244, lng: 3.3792, radius: 10.0 },
    Kano: { lat: 11.9914, lng: 8.5311, radius: 10.0 },
    Kaduna: { lat: 10.5222, lng: 7.4384, radius: 10.0 },
    Katsina: { lat: 12.9914, lng: 7.6011, radius: 10.0 },
    Jigawa: { lat: 12.57, lng: 9.56, radius: 10.0 },
    Yobe: { lat: 12.0, lng: 11.5, radius: 10.0 },
    Borno: { lat: 11.8333, lng: 13.15, radius: 10.0 },
    Adamawa: { lat: 9.3273, lng: 12.3984, radius: 10.0 },
    Taraba: { lat: 8.8833, lng: 11.3667, radius: 10.0 },
    Gombe: { lat: 10.2897, lng: 11.1673, radius: 10.0 },
    Bauchi: { lat: 10.3158, lng: 9.8444, radius: 10.0 },
    Plateau: { lat: 9.8965, lng: 8.8583, radius: 10.0 },
    Nasarawa: { lat: 8.5, lng: 8.0, radius: 10.0 },
    Niger: { lat: 9.6, lng: 6.55, radius: 10.0 },
    Kebbi: { lat: 12.45, lng: 4.2, radius: 10.0 },
    Sokoto: { lat: 13.0667, lng: 5.2333, radius: 10.0 },
    Zamfara: { lat: 12.1667, lng: 6.1667, radius: 10.0 },
    Kogi: { lat: 7.8, lng: 6.7333, radius: 10.0 },
    Kwara: { lat: 8.5, lng: 4.55, radius: 10.0 },
    Oyo: { lat: 7.85, lng: 3.9333, radius: 10.0 },
    Osun: { lat: 7.75, lng: 4.5667, radius: 10.0 },
    Ondo: { lat: 7.25, lng: 5.2, radius: 10.0 },
    Ekiti: { lat: 7.6333, lng: 5.2167, radius: 10.0 },
    Ogun: { lat: 7.1667, lng: 3.35, radius: 10.0 },
    Edo: { lat: 6.3176, lng: 5.6145, radius: 10.0 },
    Delta: { lat: 5.5, lng: 5.75, radius: 10.0 },
    Rivers: { lat: 4.75, lng: 7.0, radius: 10.0 },
    Bayelsa: { lat: 4.75, lng: 6.25, radius: 10.0 },
    "Akwa Ibom": { lat: 5.0, lng: 7.85, radius: 10.0 },
    "Cross River": { lat: 5.5, lng: 8.0, radius: 10.0 },
    Ebonyi: { lat: 6.25, lng: 8.1, radius: 10.0 },
    Enugu: { lat: 6.45, lng: 7.5, radius: 10.0 },
    Anambra: { lat: 6.2, lng: 7.0, radius: 10.0 },
    Imo: { lat: 5.5, lng: 7.0, radius: 10.0 },
    Abia: { lat: 5.5, lng: 7.5, radius: 10.0 },
  };

  // Calculate distance between two points using Haversine formula
  const calculateDistance = (
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ): number => {
    const R = 6371; // Earth's radius in kilometers
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // Find the closest state
  let closestState = "all";
  let minDistance = Infinity;

  for (const [state, coords] of Object.entries(stateCoordinates)) {
    const distance = calculateDistance(
      latitude,
      longitude,
      coords.lat,
      coords.lng
    );

    if (distance < minDistance && distance <= coords.radius) {
      minDistance = distance;
      closestState = state;
    }
  }

  return closestState;
};

interface LogisticTableProps {
  data: Logistic[];
  currentPage: number;
  totalPages: number;
  onPageChange: (newPage: number) => void;
  itemsPerPage: number;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  setSearching: (searching: boolean) => void;
  setIsModalOpen: (isOpen: boolean) => void;
  selectedOrder: any;
  setSelectedOrder: (order: Logistic | null) => void;
  setSearchParams: any;
  searchParams: URLSearchParams;
  orderId: any;
  refetchSelectedOrder: () => void;
  selectedState: string;
  setSelectedState: (state: string) => void;
  nigerianStates: string[];
}

const adminService = new AdminService();

const LogisticTable: React.FC<LogisticTableProps> = ({
  data,
  currentPage,
  totalPages,
  onPageChange,
  itemsPerPage,
  searchTerm,
  setSearchTerm,
  setSearching,
  setIsModalOpen,
  selectedOrder,
  setSelectedOrder,
  setSearchParams,
  searchParams,
  orderId,
  refetchSelectedOrder,
  selectedState,
  setSelectedState,
  nigerianStates,
}) => {
  const [visibleData, setVisibleData] = useState<Logistic[]>([]);
  // const [selectedOrder, setSelectedOrder] = useState<Logistic | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>("all");
  // const [searchTerm, setSearchTerm] = useState<string>("");
  const [filteredData, setFilteredData] = useState<Logistic[]>([]);
  const [totalCost, setTotalCost] = useState<number>(0);
  const [isLoadingOrder, setIsLoadingOrder] = useState(false);

  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (selectedOrder) return;
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      let filtered = data;

      // Filter by status
      if (filterStatus !== "all") {
        filtered = filtered.filter(
          (logistic) => logistic.order_status === filterStatus
        );
      }

      // Filter by state
      if (selectedState !== "all") {
        filtered = filtered.filter((logistic) => {
          // Use coordinates from pickup_location if available
          if (
            logistic.pickup_location?.longitude &&
            logistic.pickup_location?.latitude
          ) {
            try {
              const longitude = logistic.pickup_location.longitude;
              const latitude = logistic.pickup_location.latitude;

              // Get state from coordinates
              const stateFromCoordinates = getStateFromCoordinates(
                latitude,
                longitude
              );

              if (
                stateFromCoordinates !== "all" &&
                stateFromCoordinates === selectedState
              ) {
                return true;
              }
            } catch (error) {
              console.warn("Error processing pickup coordinates:", error);
            }
          }

          // Fallback to address-based filtering if no coordinates
          const pickupAddress =
            logistic.pickup_location.address?.toLowerCase() || "";
          const receiverAddress =
            logistic.receiver.address.address?.toLowerCase() || "";
          return (
            pickupAddress.includes(selectedState.toLowerCase()) ||
            receiverAddress.includes(selectedState.toLowerCase())
          );
        });
      }

      // Search logic
      if (searchTerm.trim()) {
        setSearching(true);
        const searchTermLower = searchTerm.toLowerCase();
        filtered = filtered.filter((logistic) => {
          const isDateMatch = new Date(logistic.created_at)
            .toLocaleDateString()
            .includes(searchTerm);
          const isTrackingIdMatch = logistic.trackingId
            .toLowerCase()
            .includes(searchTermLower);
          const isReceiverNameMatch = logistic.receiver.name
            .toLowerCase()
            .includes(searchTermLower);
          const isAddressMatch = logistic.pickup_location.address
            .toLowerCase()
            .includes(searchTermLower);
          const isAgentMatch = logistic.agentId
            .toLowerCase()
            .includes(searchTermLower);
          const isOrderStatusMatch = logistic.order_status
            .toLowerCase()
            .includes(searchTermLower);

          return (
            isTrackingIdMatch ||
            isReceiverNameMatch ||
            isAddressMatch ||
            isDateMatch ||
            isAgentMatch ||
            isOrderStatusMatch
          );
        });
      } else {
        setSearching(false);
      }

      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = Math.min(startIndex + itemsPerPage, filtered.length);

      setFilteredData(filtered);
      setVisibleData(filtered.slice(startIndex, endIndex));
      setTotalCost(filtered.reduce((sum, logistic) => sum + logistic.total, 0));
    }, 300); // Debounce delay

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [
    searchTerm,
    filterStatus,
    selectedState,
    data,
    currentPage,
    itemsPerPage,
    selectedOrder,
    setSearching,
  ]);

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const handleRowClick = (logistic: Logistic) => {
    setSelectedOrder(logistic);
    setIsModalOpen(true);
    setSearchParams((prev: any) => {
      prev.set("orderId", logistic._id);
      return prev;
    });
  };

  const closeModal = () => {
    setSelectedOrder(null);
    setIsModalOpen(false);
    searchParams.delete("orderId");
    setSearchParams(searchParams);
    refetchSelectedOrder();
  };

  const handleDownload = () => {
    const csvRows = [
      [
        "Tracking ID",
        "Pickup Location",
        "Receiver Name",
        "Cost",
        "Order Status",
        "Picked Up Time",
        "Created At",
        "Delivered Time",
        "Enroute Time",
        "Arrived Time",
        "Tax",
        "Total",
        "Agent ID",
        "User ID",
        "Description",
        "Payment Method",
      ],
      ...filteredData.map((logistic) => [
        logistic.trackingId,
        `${logistic.pickup_location.address}`, // Now only showing address
        logistic.receiver.name,
        logistic.cost.toFixed(2),
        logistic.order_status,
        logistic.picked_up_time,
        logistic.created_at,
        logistic.delivered_time,
        logistic.enroute_time,
        logistic.arrived_time,
        logistic.tax.toFixed(2),
        logistic.total.toFixed(2),
        logistic.agentId,
        logistic.userId,
        logistic.description || "",
        logistic.payment_method,
      ]),
    ];

    const csvContent =
      "data:text/csv;charset=utf-8," +
      csvRows.map((e) => e.join(",")).join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "logistics.csv");
    document.body.appendChild(link);
    link.click();
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  useEffect(() => {
    if (orderId && !selectedOrder) {
      setIsLoadingOrder(true);
      adminService
        .getParcelDetails(orderId)
        .then((order) => setSelectedOrder(order))
        .catch(() => {
          // Optionally handle not-found state
        })
        .finally(() => setIsLoadingOrder(false));
    }
  }, [orderId]);

  return (
    <div className="w-full leading-normal bg-amber-50 bg-opacity-25 rounded-tl-xl rounded-tr-xl border rounded-xl overflow-x-hidden">
      <div className="flex flex-wrap justify-between items-center gap-3 p-5 bg-white rounded-lg shadow-sm min-w-0">
        {/* Search + Download */}
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="text"
            placeholder="Search by Tracking ID, Receiver, Address, Date, Agent or Status"
            value={searchTerm}
            onChange={handleSearch}
            className="border border-gray-300 focus:border-blue-400 focus:ring-blue-400 rounded-lg px-4 py-2 text-sm w-72 shadow-sm"
          />
          <button
            onClick={handleDownload}
            className="bg-yellow-400 hover:bg-yellow-500 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 shadow-sm"
          >
            <FontAwesomeIcon icon={faDownload} />
            Download
          </button>
        </div>

        {/* Filter */}
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          <span className="text-gray-600 text-sm font-medium whitespace-nowrap">
            Filter by:
          </span>
          <div className="w-24 min-w-0">
            <CustomSelect
              value={filterStatus}
              onChange={(value) => setFilterStatus(value as string)}
              options={[
                { label: "All", value: "all" },
                { label: "Delivered", value: "delivered" },
                { label: "Pending", value: "pending" },
                { label: "Cancelled", value: "cancelled" },
                { label: "Location", value: "location" },
              ]}
              placeholder="Status"
            />
          </div>
          <span className="text-gray-600 text-sm font-medium whitespace-nowrap">
            State:
          </span>
          <div className="w-32 min-w-0">
            <CustomSelect
              value={selectedState}
              onChange={(value) => setSelectedState(value as string)}
              options={nigerianStates.map((state) => ({
                label: state === "all" ? "All States" : state,
                value: state,
              }))}
              placeholder="Select State"
            />
          </div>
        </div>

        {/* Income */}
        <div className="flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 rounded-lg text-sm font-semibold shadow-sm">
          <span>Total Income:</span>
          <span>
            {" "}
            ₦
            {totalCost.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm text-left">
          <thead className="bg-amber-100/70 text-left text-xs font-semibold text-amber-900 uppercase tracking-wider">
            <tr>
              <th className="px-5 py-3 border-b border-amber-200 w-[60px]">
                S/N
              </th>
              <th className="px-5 py-3 border-b border-amber-200 whitespace-nowrap">
                Tracking ID
              </th>
              <th className="px-5 py-3 border-b border-amber-200">Date</th>
              <th className="px-5 py-3 border-b border-amber-200 whitespace-nowrap">
                Pickup Location
              </th>
              <th className="px-5 py-3 border-b border-amber-200 whitespace-nowrap">
                Drop Off Location
              </th>
              <th className="px-5 py-3 border-b border-amber-200 whitespace-nowrap">
                Receiver Name
              </th>
              <th className="px-5 py-3 border-b border-amber-200">status</th>
              <th className="px-5 py-3 border-b border-amber-200 whitespace-nowrap">
                View More
              </th>
            </tr>
          </thead>
          <tbody>
            {visibleData.map((logistic, index) => (
              <tr
                key={logistic.trackingId}
                className="hover:bg-amber-50/80"
                onClick={() => handleRowClick(logistic)}
              >
                <td className="px-5 py-3 border-b border-amber-100">
                  {(currentPage - 1) * itemsPerPage + index + 1}
                </td>
                <td className="px-5 py-3 border-b border-amber-100">
                  {logistic?.trackingId}
                </td>
                <td className="px-5 py-3 border-b border-amber-100 whitespace-nowrap">
                  {format(new Date(logistic?.created_at), "PP")}{" "}
                </td>
                <td className="px-5 py-3 border-b border-amber-100">
                  <span
                    className="block max-w-[200px] truncate cursor-pointer"
                    title={logistic?.pickup_location?.address || "No address"}
                  >
                    {logistic?.pickup_location?.address || "—"}
                  </span>
                </td>
                <td className="px-5 py-3 border-b border-amber-100">
                  <span
                    className="block max-w-[200px] truncate cursor-pointer"
                    title={logistic?.receiver?.address?.address || "No address"}
                  >
                    {logistic?.receiver?.address?.address || "—"}
                  </span>
                </td>

                <td className="px-5 py-3 border-b border-amber-100">
                  <span
                    className="block max-w-[200px] truncate cursor-pointer"
                    title={logistic?.receiver.name || "No receiver name"}
                  >
                    {logistic?.receiver.name}
                  </span>
                </td>
                <td className="px-5 py-3 border-b border-amber-100">
                  {logistic?.order_status}
                </td>
                <td className="px-5 py-3 border-b border-amber-100">
                  <img src={removeRedEye} alt="View" className="w-6 h-6" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex justify-between items-center p-4">
        <button
          onClick={handlePreviousPage}
          disabled={currentPage === 1}
          className="bg-blue-500 text-white px-4 py-2 rounded-lg disabled:bg-gray-300"
        >
          Previous
        </button>
        <span className="self-center">
          {currentPage} of {totalPages}
        </span>
        <button
          onClick={handleNextPage}
          disabled={currentPage === totalPages}
          className="bg-blue-500 text-white px-4 py-2 rounded-lg disabled:bg-gray-300"
        >
          Next
        </button>
      </div>

      {(selectedOrder || isLoadingOrder) && (
        <LogisticDetails
          logistic={selectedOrder}
          onClose={closeModal}
          handleStatusChange={function (
            logistic: Logistic,
            newStatus: string
          ): void {
            throw new Error("Function not implemented.");
          }}
          successMessage={null}
          error={null}
          refetchSelectedOrder={refetchSelectedOrder}
        />
      )}
    </div>
  );
};

export default LogisticTable;
